import {
    BaseWalletAdapter,
    WalletNotConnectedError,
    WalletReadyState,
} from "@solana/wallet-adapter-base";
import { VersionedTransaction } from "@solana/web3.js";
import nacl from "tweetnacl";
import { anonymousAuthReady, auth } from "../firebaseConfig";
import { openPublishSessionForWallet } from "./auth";
import { clearEmailWalletSession, restoreEmailWalletSession, saveEmailWalletSession } from "./emailWalletSession";

export const NATIVE_EMAIL_WALLET_NAME = "Muulti Email Wallet";

const EMAIL_WALLET_ICON =
    "data:image/svg+xml;utf8," +
    encodeURIComponent(
        '<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64"><rect width="64" height="64" rx="14" fill="#14363a"/><path d="M14 22h36v24H14z" fill="#f7f1df"/><path d="M15 23l17 13 17-13" fill="none" stroke="#14363a" stroke-width="4" stroke-linejoin="round"/><circle cx="49" cy="45" r="9" fill="#e5b94f"/><path d="M45 45h8M49 41v8" stroke="#14363a" stroke-width="3" stroke-linecap="round"/></svg>'
    );

export class NativeEmailWalletAdapter extends BaseWalletAdapter {
    name = NATIVE_EMAIL_WALLET_NAME;
    url = "https://muultispace.com";
    icon = EMAIL_WALLET_ICON;
    readyState = WalletReadyState.Loadable;
    supportedTransactionVersions = new Set(["legacy", 0]);
    connecting = false;

    _keypair = null;
    _connected = false;
    _sessionRevision = 0;
    _restorePromise = null;
    _connectPromise = null;

    get hasSession() {
        return !!this._keypair;
    }

    get publicKey() {
        return this._keypair?.publicKey || null;
    }

    get connected() {
        return this._connected;
    }

    setSession(keypair) {
        if (!keypair) return this.clearSession();
        this._sessionRevision += 1;
        this._keypair = keypair;
        this._connected = false;
        this.emit("sessionChange", true);
    }

    clearSession() {
        this._sessionRevision += 1;
        this._keypair = null;
        this._connected = false;
        this.connecting = false;
        this._restorePromise = null;
        this._connectPromise = null;
        this.emit("sessionChange", false);
        this.emit("disconnect");
        return clearEmailWalletSession().catch((error) => {
            console.warn("Could not clear email wallet session storage:", error);
        });
    }

    async restoreSession() {
        if (this._keypair) return true;
        if (this._restorePromise) return this._restorePromise;
        const revision = this._sessionRevision;
        const restoring = restoreEmailWalletSession().then((keypair) => {
            if (revision !== this._sessionRevision || !keypair) return false;
            this.setSession(keypair);
            return true;
        });
        this._restorePromise = restoring;
        try {
            return await restoring;
        } finally {
            if (this._restorePromise === restoring) this._restorePromise = null;
        }
    }

    async connect() {
        if (this._connected) return;
        if (this._connectPromise) return this._connectPromise;
        this.connecting = true;
        const connecting = (async () => {
            if (!this._keypair) {
                const beforeRestore = this._sessionRevision;
                const restored = await this.restoreSession();
                if (!restored && beforeRestore !== this._sessionRevision) return;
            }
            const revision = this._sessionRevision;
            const keypair = this._keypair;
            const isCurrent = () => revision === this._sessionRevision && this._keypair === keypair;
            try {
                if (!keypair) throw new Error("Sign in with email before connecting the Muulti email wallet.");
                await anonymousAuthReady;
                if (!isCurrent()) return;
                const publicKey = keypair.publicKey.toBase58();
                if (auth.currentUser?.uid !== publicKey) {
                    await openPublishSessionForWallet(this, 'us-central1', { isCurrent });
                }
                if (!isCurrent()) return;
                if (auth.currentUser?.uid !== publicKey) {
                    throw new Error("Email wallet authentication did not match its public key.");
                }
                try {
                    await saveEmailWalletSession(keypair, isCurrent);
                } catch (error) {
                    // A browser storage restriction must not break the active login.
                    console.warn("Email wallet refresh persistence is unavailable:", error);
                }
                if (!isCurrent()) return;
                this._connected = true;
                this.emit("connect", keypair.publicKey);
            } catch (error) {
                if (!isCurrent()) return;
                this.emit("error", error);
                throw error;
            }
        })();
        this._connectPromise = connecting;
        try {
            return await connecting;
        } finally {
            if (this._connectPromise === connecting) {
                this._connectPromise = null;
                this.connecting = false;
            }
        }
    }

    async disconnect() {
        await this.clearSession();
    }

    async signMessage(message) {
        if (!this._keypair || (!this._connected && !this.connecting)) throw new WalletNotConnectedError();
        return nacl.sign.detached(message, this._keypair.secretKey);
    }

    async signTransaction(transaction) {
        if (!this._keypair || !this._connected) throw new WalletNotConnectedError();

        if (transaction instanceof VersionedTransaction) {
            transaction.sign([this._keypair]);
            return transaction;
        }

        transaction.partialSign(this._keypair);
        return transaction;
    }

    async signAllTransactions(transactions) {
        return Promise.all(transactions.map((transaction) => this.signTransaction(transaction)));
    }

    async sendTransaction(transaction, connection, options = {}) {
        if (!this._keypair || !this._connected) throw new WalletNotConnectedError();

        const { signers, ...sendOptions } = options || {};
        if (transaction instanceof VersionedTransaction) {
            if (Array.isArray(signers) && signers.length > 0) {
                transaction.sign(signers);
            }
        } else {
            await this.prepareTransaction(transaction, connection, sendOptions);
            if (Array.isArray(signers) && signers.length > 0) {
                transaction.partialSign(...signers);
            }
        }

        const signed = await this.signTransaction(transaction);
        return connection.sendRawTransaction(signed.serialize(), sendOptions);
    }
}

export const nativeEmailWalletAdapter = new NativeEmailWalletAdapter();
